import React, { useContext, useState } from 'react';
import Employee from 'src/interfaces/api/Employee';
import useAuth from 'src/hooks/api/useAuth';
import employeesApi from 'src/config/api/employee';
import { getJsonResponse } from '../../../utils/ApiUtils';

interface EmployeesContextValues {
  fetchEmployee: (employeeId: Employee['id']) => Promise<Employee>;
}

const Context = React.createContext<EmployeesContextValues | undefined>(undefined);

let isCalling = new Map();

const EmployeesProvider: React.FC = (props) => {
  const { fetch } = useAuth();

  const [employees, setEmployees] = useState<Record<Employee['id'], Employee>>({});

  const fetchEmployee = async (employeeId: Employee['id']): Promise<Employee> => {
    if (Object.keys(employees).includes(employeeId)) {
      return employees[employeeId];
    }

    const { endpoint, ...options } = employeesApi.get(employeeId);

    let isThisAPICalling = isCalling.get(employeeId);

    if (!isThisAPICalling) {
      isThisAPICalling = fetch(endpoint, options)
        .then((response) => {
          isCalling.set(employeeId, null);
          return getJsonResponse<Employee>(response);
        })
        .then((employee: Employee) => {
          setEmployees(Object.assign({}, employee, { [employee.id]: employee }));

          return employee;
        });

      isCalling.set(employeeId, isThisAPICalling);
    }

    return await isThisAPICalling;
  };

  const value: EmployeesContextValues = {
    fetchEmployee,
  };

  return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const useEmployees = (): EmployeesContextValues => {
  const value = useContext(Context);

  if (value === undefined) {
    throw new Error('You must wrap components that call `useEmployees` by a `EmployeesProvider`');
  }

  return value;
};

export { EmployeesProvider };
export default useEmployees;
